import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiDelete, apiGet, apiPost, ApiError } from "../api";
import type { ID } from "../id";
import type { Person } from "./memberships";
import type { PersonListItem } from "./persons";
import type { DuplicateReason } from "../duplicateReasons";

// A suggestion from GET /persons/duplicates (service/duplicates). Derived on
// every read: fixing a typo in either record makes it disappear.
export interface DuplicatePair {
  a: PersonListItem;
  b: PersonListItem;
  reasons: DuplicateReason[];
  // „Pravděpodobně totožní" when true, „Možná totožní" otherwise.
  strong: boolean;
}

// A stored „Nejsou totožní".
export interface DistinctDecision {
  id: ID;
  a: Person;
  b: Person;
  reason: string;
  decided_by: string;
  created_at: Dayjs;
}

// Under ["persons"], so every person edit (which invalidates that prefix)
// re-runs the check: a fixed typo drops its pair at once.
const DUPLICATES_KEY = ["persons", "duplicates"] as const;
const DISTINCT_KEY = ["persons", "distinct"] as const;

export function usePersonDuplicates() {
  return useQuery<DuplicatePair[]>({
    queryKey: DUPLICATES_KEY,
    queryFn: async () => (await apiGet<{ data: DuplicatePair[] }>("/persons/duplicates")).data,
  });
}

export function useDistinctDecisions() {
  return useQuery<DistinctDecision[]>({
    queryKey: DISTINCT_KEY,
    queryFn: async () => (await apiGet<{ data: DistinctDecision[] }>("/persons/distinct")).data,
  });
}

export interface MarkDistinctPayload {
  person_a_id: ID;
  person_b_id: ID;
  reason: string;
}

export function useMarkDistinct() {
  const qc = useQueryClient();
  return useMutation<{ id: ID }, ApiError, MarkDistinctPayload>({
    mutationFn: (payload) => apiPost<{ id: ID }>("/persons/distinct", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DUPLICATES_KEY });
      qc.invalidateQueries({ queryKey: DISTINCT_KEY });
    },
    // The dialog renders the refusal in its error slot.
    meta: { suppressErrorToast: true },
  });
}

export function useRevokeDistinct() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (id) => apiDelete<void>(`/persons/distinct/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DUPLICATES_KEY });
      qc.invalidateQueries({ queryKey: DISTINCT_KEY });
    },
    // useConfirm shows the refusal beside the button pressed.
    meta: { suppressErrorToast: true },
  });
}
